import React, {useEffect, useRef, useState} from 'react'
import ApexCharts, {ApexOptions} from 'apexcharts'
import {getDailyRamNameApi} from '../../../modules/Dashboard/DashboardCURD'

/* ─── Types ─── */
interface DailyRamNameItem {
  dayDate: string
  dayName: string
  dayLabel: string
  totalCount: number
}
interface DailyRamNameResp {
  isSuccess: boolean
  message: string
  data: DailyRamNameItem[]
}
interface IState {
  loading: boolean
  items: DailyRamNameItem[]
  error: string
}

/* ─── API ─── */
async function fetchDailyActivity(memberId = 0, days = 7): Promise<DailyRamNameResp> {
  const response = await getDailyRamNameApi(memberId, days)
  return response.data
}

// Add this helper at the top of the file, outside the component
function formatIndian(v: number): string {
  if (v >= 10_000_000) return `${(v / 10_000_000).toFixed(2)} Cr`
  if (v >= 100_000) return `${(v / 100_000).toFixed(2)} L`
  //   if (v >= 1_000)      return `${(v / 1_000).toFixed(1)} K`
  return v.toLocaleString('en-IN')
}

/* ─── Chart options ─── */
function buildChartOptions(categories: string[], series: number[]): ApexOptions {
  return {
    chart: {
      type: 'bar',
      height: 220,
      toolbar: {show: false},
      zoom: {enabled: false},
      fontFamily: 'inherit',
      animations: {enabled: true, easing: 'easeinout', speed: 700},
    },
    plotOptions: {bar: {borderRadius: 6, columnWidth: '48%'}},
    dataLabels: {
      enabled: true,
      offsetY: -6,
      style: {
        fontSize: '11px',
        colors: ['#111827'],
        fontWeight: 600,
      },
      formatter: (val: number) => formatIndian(val),
    },
    fill: {
      type: 'gradient',
      gradient: {
        shade: 'light',
        type: 'vertical',
        shadeIntensity: 0.25,
        gradientToColors: ['#e74c7d'],
        inverseColors: false,
        opacityFrom: 1,
        opacityTo: 0.8,
      },
    },
    colors: ['#f97316'], // kesari orange
    stroke: {show: true, width: 0},
    xaxis: {
      categories,
      axisBorder: {show: false},
      axisTicks: {show: false},
      labels: {
        style: {
          colors: '#9ca3af',
          fontSize: '12px',
          fontFamily: 'inherit',
        },
        formatter: function (value: string) {
          const parts = value.split('|')
          return [parts[0], parts[1]]
        },
      },
    },
    yaxis: {
      labels: {
        style: {colors: '#9ca3af', fontSize: '11px'},
        formatter: (v) => {
          if (v >= 10_000_000) return `${(v / 10_000_000).toFixed(1)}Cr`
          if (v >= 100_000) return `${(v / 100_000).toFixed(1)}L`
          if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`
          return String(v)
        },
      },
    },
    grid: {
      borderColor: '#e5e7eb',
      strokeDashArray: 3,
      yaxis: {
        lines: {show: true}, // ✅ horizontal lines
      },
      xaxis: {
        lines: {show: false},
      },
    },
    tooltip: {
      y: {formatter: (v) => v.toLocaleString('en-IN')},
      style: {fontSize: '12px', fontFamily: 'inherit'},
    },
    series: [{name: 'Ram Name Count', data: series}],
    states: {
      hover: {filter: {type: 'lighten', value: 0.1}},
      active: {filter: {type: 'none'}},
    },
  }
}

/* ─── Component ─── */
const DailyActivityWidget: React.FC<{className?: string; memberId?: number; days?: number}> = ({
  className = '',
  memberId = 0,
  days = 7,
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null)
  const chartInstance = useRef<ApexCharts | null>(null)
  const [state, setState] = useState<IState>({loading: true, items: [], error: ''})

  useEffect(() => {
    setState((s) => ({...s, loading: true, error: ''}))
    fetchDailyActivity(memberId, days)
      .then((resp) => {
        if (resp.isSuccess) setState({loading: false, items: resp.data ?? [], error: ''})
        else setState({loading: false, items: [], error: resp.message})
      })
      .catch((err) => setState({loading: false, items: [], error: String(err)}))
  }, [memberId, days])

  useEffect(() => {
    if (state.loading || !chartRef.current) return
    const categories = state.items.map(
      (i) => `${i.dayLabel || i.dayDate}|${i.dayName?.substring(0, 3)}`
    )
    const series = state.items.map((i) => Number(i.totalCount) || 0)
    if (chartInstance.current) {
      chartInstance.current.updateOptions(buildChartOptions(categories, series))
    } else {
      chartInstance.current = new ApexCharts(
        chartRef.current,
        buildChartOptions(categories, series)
      )
      chartInstance.current.render()
    }
    return () => {
      chartInstance.current?.destroy()
      chartInstance.current = null
    }
  }, [state.loading, state.items])

  const total = state.items.reduce((sum, i) => sum + Number(i.totalCount || 0), 0)
  const peak = state.items.reduce(
    (max, i) => (Number(i.totalCount) > Number(max.totalCount) ? i : max),
    state.items[0] || ({totalCount: 0, dayLabel: '—'} as DailyRamNameItem)
  )
  const today = state.items.reduce(
    (latest, i) => (i.dayDate > latest.dayDate ? i : latest),
    state.items[0] || ({totalCount: 0, dayLabel: '—'} as DailyRamNameItem)
  )
  return (
    <>
      <style>{`
        @keyframes daw-shimmer { 0%,100%{opacity:1} 50%{opacity:.35} }
        .daw-shimmer { animation:daw-shimmer 1.4s ease-in-out infinite; background:#f3f4f6; border-radius:8px; }
        .daw-shimmer-delay-1 { animation-delay:0.1s; }
        .daw-shimmer-delay-2 { animation-delay:0.2s; }
        .daw-shimmer-delay-3 { animation-delay:0.3s; }
        .daw-shimmer-delay-4 { animation-delay:0.4s; }
        .daw-shimmer-delay-5 { animation-delay:0.5s; }
        .daw-shimmer-delay-6 { animation-delay:0.6s; }
        .daw-pill-total { background:#fff7ed; border:1px solid #fed7aa; }
        .daw-pill-today  { background:#fffbeb; border:1px solid #9ae81f; }
        .daw-pill-peak  { background:#fffbeb; border:1px solid #fde68a; }
      `}</style>

      <div className={`card border-0 shadow-sm rounded-4 overflow-hidden ${className}`}>
        {/* ── Header ── */}
        <div
          className='card-header border-0 py-3 px-4 d-flex flex-wrap align-items-center justify-content-between gap-2'
          style={{background: 'linear-gradient(135deg,#f97316 0%,#fb923c 100%)'}}
        >
          <div>
            <h3 className='card-title fw-bolder text-white mb-1 fs-5 mb-0'>
              <i className='bi bi-activity me-2' />
              Daily Ram Name Activity
            </h3>
            <p className='text-white mb-0 small' style={{opacity: 0.8}}>
              Last {days} days — Digital Jaap
            </p>
          </div>
          <span
            className='fw-bold small'
            style={{
              background: 'rgba(255,255,255,0.18)',
              border: '1px solid rgba(255,255,255,0.3)',
              color: '#fff',
              borderRadius: 20,
              padding: '3px 12px',
            }}
          >
            {days}D
          </span>
        </div>

        {/* ── Summary pills ── */}
        <div className='px-3 px-md-4 pt-3 pt-md-4'>
          <div className='row g-2 g-md-3'>
            <div className='col-4'>
              {state.loading ? (
                <div className='daw-shimmer w-100' style={{height: 62}} />
              ) : (
                <div className='daw-pill-total rounded-3 px-3 py-2 h-100'>
                  <p className='fw-bold mb-0 fs-4 lh-1' style={{color: '#ea580c'}}>
                    {formatIndian(total)}
                  </p>
                  <small className='text-muted'>Total Count</small>
                </div>
              )}
            </div>
            <div className='col-4'>
              {state.loading ? (
                <div className='daw-shimmer daw-shimmer-delay-1 w-100' style={{height: 62}} />
              ) : (
                <div className='daw-pill-peak rounded-3 px-3 py-2 h-100'>
                  <p className='fw-bold mb-0 fs-4 lh-1' style={{color: '#1655a3'}}>
                    {formatIndian(Number(today?.totalCount || 0))}
                  </p>
                  <small className='text-muted'>Today · {today?.dayLabel || '—'}</small>
                </div>
              )}
            </div>
            <div className='col-4'>
              {state.loading ? (
                <div className='daw-shimmer daw-shimmer-delay-1 w-100' style={{height: 62}} />
              ) : (
                <div className='daw-pill-peak rounded-3 px-3 py-2 h-100'>
                  <p className='fw-bold mb-0 fs-4 lh-1' style={{color: '#16a34a'}}>
                    {formatIndian(Number(peak?.totalCount || 0))}
                  </p>
                  <small className='text-muted'>Peak · {peak?.dayLabel || '—'}</small>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Chart ── */}
        <div className='card-body pt-3 pb-4 px-3 px-md-4'>
          {state.loading ? (
            <div className='d-flex align-items-end gap-2 w-100' style={{height: 220}}>
              {[60, 85, 45, 95, 70, 55, 80].map((h, i) => (
                <div
                  key={i}
                  className={`daw-shimmer flex-fill daw-shimmer-delay-${Math.min(i, 6)}`}
                  style={{height: `${h}%`, borderRadius: 6}}
                />
              ))}
            </div>
          ) : state.error ? (
            <div
              className='d-flex align-items-center justify-content-center text-danger small'
              style={{height: 220}}
            >
              <i className='bi bi-exclamation-triangle me-2' />
              {state.error}
            </div>
          ) : (
            <div ref={chartRef} />
          )}
        </div>
      </div>
    </>
  )
}

export {DailyActivityWidget}
